import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, RefreshCw, Save, CheckCircle2 } from "lucide-react";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { VendorLogo } from "./VendorLogo";
import { VendorFields } from "./VendorFields";
import { LastChanged, apiError, fmtDateTime } from "./vendorUtils";

const pick = (v) => ({ name: v.name || "", website: v.website || "", category: v.category || "", description: v.description || "", status: v.status || "active" });

export function VendorGeneralTab({ vendor, onChange, canEdit }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [form, setForm] = useState(pick(vendor));
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => setForm(pick(vendor)), [vendor]);
  const dirty = JSON.stringify(form) !== JSON.stringify(pick(vendor));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name darf nicht leer sein");
    setSaving(true);
    try {
      const res = await vendorsApi.update(vendor.id, form);
      onChange(res.data);
      setSavedAt(new Date());
      toast.success("Änderungen gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const logoAction = async (kind, file) => {
    setLogoBusy(kind);
    try {
      const res = kind === "upload" ? await vendorsApi.uploadLogo(vendor.id, file) : await vendorsApi.refreshLogo(vendor.id);
      onChange(res.data);
      toast.success(kind === "upload" ? "Logo gespeichert" : "Neues Logo gefunden und gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Logo konnte nicht gespeichert werden"));
    } finally {
      setLogoBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3"><CardTitle className="text-base">Stammdaten</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <VendorFields form={form} setForm={setForm} prefix="general" />
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
            <LastChanged item={vendor} testId="vendor-general-last-changed" />
            <div className="flex items-center gap-3">
              {savedAt && !dirty && <span className="text-xs text-green-600 flex items-center gap-1" data-testid="vendor-general-saved"><CheckCircle2 className="w-3.5 h-3.5" />Gespeichert</span>}
              {canEdit && <Button onClick={save} disabled={saving || !dirty} data-testid="vendor-general-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Speichern
              </Button>}
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Logo</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <VendorLogo vendor={vendor} size="w-24 h-24" text="text-4xl" />
            <div className="text-xs text-muted-foreground space-y-1" data-testid="vendor-logo-info">
              <p>{vendor.has_logo ? (vendor.logo_source === "upload" ? "Manuell hochgeladen" : "Automatisch gefunden") : "Kein Logo gefunden"}</p>
              {vendor.logo_updated_at && <p>Stand: {fmtDateTime(vendor.logo_updated_at)}</p>}
              <p>Nur intern sichtbar.</p>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && logoAction("upload", e.target.files[0])} data-testid="vendor-logo-file" />
          {canEdit && <div className="grid gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!!logoBusy} data-testid="vendor-logo-upload-btn">
              {logoBusy === "upload" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {vendor.has_logo ? "Logo ersetzen" : "Logo hochladen"}
            </Button>
            <Button variant="outline" onClick={() => logoAction("refresh")} disabled={!!logoBusy || !vendor.website} data-testid="vendor-logo-refresh-btn">
              {logoBusy === "refresh" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Logo neu suchen
            </Button>
          </div>}
        </CardContent>
      </Card>
    </div>
  );
}
